<script setup lang="ts">
import { normalizeNumberQueryParameter } from '@shared/utils/queryNormalizers';
import DOMPurify from 'isomorphic-dompurify';
import { formatPublicTs } from '@shared/utils/formatPublicTs';
import { useImageUrl } from '@shared/composables/useImageUrl';

definePageMeta({
  middleware: 'pages-public-article-news',
});

const router = useRouter();
const route = useRoute();

const { createArticleImageUrl } = useImageUrl();

const {
  tag,
  keyword,
  heading,
  categoryId,
  order,
  categoryName,
  isPrArticle,
  configureBreadcrumbs,
} = await usePublicArticleNewsHelpers(route);

const scrollTargetRef = ref<HTMLDivElement>();

function changePage(page: number) {
  router.push({
    query: {
      ...route.query,
      page,
    },
  });
}

const currentPage = computed(() => {
  return normalizeNumberQueryParameter(route.query.page as string, 1);
});

const localKeyword = ref(route.query.s as string);

const { data, refresh } = await useFetch('/api/kiwi_user/articles', {
  query: {
    folder_id: 100,
    limit: 10,
    order_by: order.value === 'rank' ? 'page_view' : 'public_ts',
    direction: 'desc',
    page: currentPage,
    keyword,
    category: categoryId,
    tag,
  },
});

watch(
  () => route.query,
  async () => {
    await refresh();
    configureBreadcrumbs();
    scrollTargetRef?.value?.scrollIntoView({
      behavior: 'smooth',
    });
  },
);

const articles = computed(() => {
  return data.value?.articles ?? [];
});

const totalPages = computed(() => {
  return data?.value?.meta.totalPages ?? 0;
});

async function submitSearch() {
  router.push({
    query: {
      page: 1,
      s: DOMPurify.sanitize(localKeyword.value),
    },
  });
}

const lead = computed(() => {
  if (order.value === 'rank') {
    return '今週話題になった美容ニュースやダイエット方法、正しい食べ方などを、栄養学・ダイエット指導の専門家が分かりやすく解説します。';
  }

  if (keyword.value) {
    return `${keyword.value}を、栄養学・ダイエット指導の専門家が分かりやすく解説します。`;
  }

  if (categoryId.value) {
    return `${categoryName.value}の話題のニュースを、栄養学・ダイエット指導の専門家が分かりやすく解説します。`;
  }

  return '話題の美容ニュースやダイエット方法、正しい食べ方などを、栄養学・ダイエット指導の専門家が分かりやすく解説します。';
});

const categoryLead = computed<string | null>(() => {
  switch (categoryName.value) {
    case '食・レシピ':
      return '「食事」とは人間にとって生きる上でとても重要な行為です。毎日の食事から取り入れたものが体づくりに直結していきます。健康な体を保っていくためには、毎日栄養バランスの良い食事をとることが重要不可欠。そんな食材に関する知識や栄養、美味しく食べられるレシピまで幅広くお届けしていきます。';
    case 'エクササイズ':
      return 'エクササイズとは、筋肉の強化や維持、健康を保つための肉体的な運動、美容、ストレス解消を目的とする運動などの総称です。ストレッチ、ヨガ、ウォーキング、ジョギング、水泳のほかに、さまざまなスポーツの要素を取り入れた運動も含まれます。ここでは、日常的にできる「エクササイズ」から、本格的な「エクササイズ」情報まで幅広くお届けしています。';
    case 'ビューティー':
      return '「ビューティー」とは、美しさを指す言葉。メイクやファッションだけで美しく見せるのではなく、内面、体つきから美しさを整え、保っていくためには何が必要なのでしょうか？日頃、よく口にしている食べ物の美容効果や、美しくなるための知識など、心身ともに自分のなりたい自分でいられるような「ビューティー」情報をお届けしています。';
    case '更年期対策':
      return '「更年期」とは、生理がこなくなる閉経期の前後5年、約10年間のことを指します。一般的には45～55歳頃にやってくるものです。この「更年期」の間は、女性ホルモンの分泌が減少し、ホルモンバランスが乱れることにより、のぼせ、めまい、肩こり、不眠などの体調不良が起こりやすくなります。人によっては症状が重く、日常生活にも支障をきたしてしまう場合も。ここでは、そんな「更年期」を乗り越えるための情報をお届けしていきます。';
    case 'ダイエット':
      return ''; // NOTE: ダイエットの場合のみ別の見た目で表示する。template内に記述。
    default:
      return null;
  }
});

useSeoMeta({
  title: heading,
  keywords:
    'ダイエット,だいえっと,瘦せない,瘦せたい,ダイエット方法,食事,カロリー,レシピ,ダイエットプラス,ダイエットプラス,管理栄養士',
  description: lead,
});

const isClickedMoreButton = ref(false);
function onClickedMoreButton() {
  isClickedMoreButton.value = true;
}
</script>

<template>
  <div class="px-2 md:px-0">
    <MoleculesSearchForm
      v-model="localKeyword"
      placeholder="ニュース・キーワード・食材"
      class="mb-5"
      @submit="submitSearch"
    />

    <div ref="scrollTargetRef" />

    <MoleculesTitleCard :heading="heading" class="mb-5">
      <div v-if="categoryLead !== null" class="bg-gray-100 p-4">
        <div v-if="categoryName === 'ダイエット'">
          <h3
            class="font-bold text-xl mb-5 border-l-[6px] pl-3 border-orange-400"
          >
            ダイエットとは
          </h3>

          <p class="mb-5">
            「ダイエット」は、「減量」ではありません。ダイエットという言葉の本来の意味は、ラテン語で「生き方」。「ダイエット」は、一時的に体重を落としたり、細くなったりすることだけを意味するのではなく、人が健康で豊かな生涯を送るための「正しく食べる」行為そのものを言います。生涯続けていく、価値ある上質な食習慣が「ダイエット」なのです。やせることは、「ダイエット」を実行したその先の事象でしかありません。
          </p>

          <div v-if="isClickedMoreButton">
            <h4
              class="font-bold text-lg mb-5 border-l-[6px] pl-3 border-orange-400"
            >
              ダイエットプラスが考える「ダイエット」とは
            </h4>
            <p>
              ダイエットプラスでは、健康で「太らない」生活を送るために、身体の機能やメカニズム、それを実行するための具体的な方法やレシピ・献立を、管理栄養士がご案内します。<br />
              身体の機能を最大限にする、あなたに最適な「正しい食事」、「太らないライフスタイル」に役立つ情報を見つけてください。「正しく食べる」ことで、充実した毎日になり、よりよい人生となるはずです。「ダイエット」の先には、様々な嬉しい変化が待っています。<br />
              <br />
              食べ物が変われば、身体が変わる。<br />
              身体が変われば、考え方が変わる。<br />
              考え方が変われば、人生までも大きく変わる<br />
              <br />
              ダイエットプラスは、多くの方にこの体験をお届けしたいと思っています。
            </p>
          </div>

          <div v-else class="text-center">
            <button
              class="shadow py-2 px-4 border border-gray-300 bg-white rounded-md"
              @click="onClickedMoreButton"
            >
              もっと見る
            </button>
          </div>
        </div>
        <div v-else>
          <h3
            class="font-bold text-xl mb-5 border-l-[6px] pl-3 border-orange-400"
          >
            {{ categoryName }} について
          </h3>

          <p>{{ categoryLead }}</p>
        </div>
      </div>

      <div class="p-4">
        <p v-if="categoryLead === null" class="mb-10" data-testid="lead">
          {{ lead }}
        </p>

        <div v-if="articles.length">
          <NuxtLink
            v-for="article in articles"
            :key="article.permalink!"
            :to="createArticleLink(article.permalink!)"
            class="flex gap-4 mb-4 hover:underline hover:opacity-70 duration-300 items-start"
          >
            <div class="shrink-0 w-[100px]">
              <img
                v-if="article.image_file"
                :src="
                  createArticleImageUrl({
                    path: article.image_file,
                    w: 150,
                    h: 150,
                  })
                "
                class="object-cover w-full h-full relative"
              />
              <AtomsPlaceholderImage
                v-else
                class="object-cover w-full h-full relative"
              />
            </div>

            <div>
              <h3 class="font-bold text-xl underline mb-3">
                {{ article.title }}
              </h3>
              <p class="mb-3">{{ sliceString(article.lead) }}</p>
              <p v-if="article.public_ts" class="text-xs text-gray-400">
                {{ formatPublicTs(article.public_ts) }}
                <span
                  v-if="isPrArticle(article)"
                  class="border border-current p-0.5 ml-1"
                  >PR</span
                >
              </p>
            </div>
          </NuxtLink>

          <MoleculesDietPlusPagination
            :current-page="currentPage"
            :total-pages="totalPages"
            @change-page="changePage"
          />
        </div>

        <div v-else class="pb-2 text-lg font-bold">
          該当する記事が見つかりませんでした
        </div>
      </div>
    </MoleculesTitleCard>

    <OrganismsNewsesByCategory class="mb-5" />

    <div class="hidden md:flex">
      <MoleculesGoogleAd
        id="div-gpt-ad-1534157217429-0"
        target="pc"
        class="w-1/2 h-[250px] shrink-0"
      />
      <MoleculesGoogleAd
        id="div-gpt-ad-1534157182002-0"
        target="pc"
        class="w-1/2 h-[250px] shrink-0"
      />
    </div>
  </div>
</template>
